import React from "react"
import { RadioGroup } from "@headlessui/react"
import classNames from "../../utils/classNames"
import Icon from "../../icon"
import img_pay_separately from "../../../assets/images/park__icon_ux__pay_separately.svg?react"
import img_pay_together from "../../../assets/images/park__icon_ux__pay_together.svg?react"
import img_rent_and_electric from "../../../assets/images/park__icon_ux__rent_and_electric.svg?react"
import img_rent from "../../../assets/images/park__icon_ux__rent.svg?react"
import img_seasonal_and_electric from "../../../assets/images/park__icon_ux__seasonal_and_electric.svg?react"
import img_seasonal from "../../../assets/images/park__icon_ux__seasonal.svg?react"
import { LONG_TERM_RESERVATION_KINDS } from "~/constants"
import { GROUP_PAYMENT_TYPES } from "~/constants/group_payment_types"
import icons from "../../icon"
import HoverTip from "./HoverTip"
import { reservationTypeExplainer } from "../Admin/Reservations/NewReservationModal/utils"
import { InformationCircleIcon } from "@heroicons/react/24/outline"

interface ComponentProps {
  label?: string
  options: Array<{ label: string; value: any }>
  selectedOption: any
  onChange?: (v: any) => void
  isLoading?: boolean
}

const ReservationTypeSelector = ({ label, options, selectedOption, onChange, isLoading = false }: ComponentProps) => {
  return (
    <RadioGroup className="w-full" value={selectedOption} data-testid="new-reservation-type-radiogroup">
      <RadioGroup.Label className={classNames("text-base", isLoading ? "text-gray-400" : "text-gray-900")}>
        {label}
      </RadioGroup.Label>

      <div className="grid grid-flow-col auto-cols-fr gap-2.5 w-full">
        {options.map((option, index) => (
          <RadioGroup.Option
            data-testid={`new-reservation-type-radiogroup-${index}`}
            key={index}
            value={option.value}
            disabled={isLoading}
            onClick={() => {
              if (isLoading) return
              onChange?.(option.value)
            }}
            className={({ checked, active }) =>
              classNames(
                "pr-4 cursor-pointer",
                "ghostinspector-new-reservation-type-" + option.value,
                isLoading ? "opacity-50 cursor-not-allowed" : checked ? "border-transparent" : "border-gray-300",
                active ? "ring-1 ring-green-600" : "",
                "relative bg-white border rounded-md shadow-sm py-2 pl-4 flex items-center focus:outline-none",
              )
            }
          >
            {({ checked, active }) => (
              <>
                <div className="relative w-full">
                  <div className="absolute -top-2.5 -right-4.5 p-2 z-50">
                    <HoverTip content={reservationTypeExplainer(option.value)} width="w-72">
                      <InformationCircleIcon
                        className={classNames(
                          "h-4 w-4", 
                          isLoading ? "text-gray-400" : checked ? "text-green-600" : "text-gray-900",
                        )}
                      />
                    </HoverTip>
                  </div>
                  
                  <div className="flex items-center gap-2.5">
                    <CampingStyleIcon
                      name={option.value}
                      className={classNames(
                        "w-8 h-8 self-center stroke-current flex-shrink-0",
                        isLoading ? "text-gray-400" : checked ? "text-green-600" : "text-gray-900",
                      )}
                    />
                    <RadioGroup.Label
                      as="span"
                      className={classNames(
                        "flex whitespace-nowrap text-wrap leading-[1.15rem]",
                        isLoading
                          ? "text-gray-400"
                          : checked
                            ? "text-green-600 font-bold"
                            : "text-gray-900 font-medium",
                      )}
                    >
                      {option.label}
                    </RadioGroup.Label>
                  </div>
                </div>
                <div
                  className={classNames(
                    active ? "border" : "border-2",
                    checked ? "border-green-600" : "border-transparent",
                    "absolute -inset-px rounded-md pointer-events-none",
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}

const svgForName = (name: string) => {
  switch (name) {
    case "single":
      return icons.SingleReservation
    case "recurring":
      return icons.RecurringReservation
    case "group":
      return icons.GroupReservation
    case "blocked":
      return Icon.BlockedReservation
    case GROUP_PAYMENT_TYPES.SEPARATE:
      return img_pay_separately
    case GROUP_PAYMENT_TYPES.TOGETHER:
      return img_pay_together
    case LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES:
      return img_rent_and_electric
    case LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY:
      return img_seasonal_and_electric
    case LONG_TERM_RESERVATION_KINDS.RENT_ONLY:
      return img_rent
    case LONG_TERM_RESERVATION_KINDS.SEASONAL_ONLY:
      return img_seasonal
    default:
      console.error("Missing icon for:", name)
      return <svg />
  }
}

const CampingStyleIcon = ({ name, className }: { name: string; className: string }) => {
  const IconSvg = svgForName(name)
  return !IconSvg?.type ? <IconSvg className={className} /> : <></>
}

export default ReservationTypeSelector
